import React, { useEffect, useMemo, useState } from 'react';
import { createTemplateRest } from '@/services/template/templateRest';
import routes from '@/routes/routes-enums';
import FormField from './form-field';
import 'react-toastify/dist/ReactToastify.css';
import { showToast } from '@/common/showToast';
import {
  GameRegistry,
  GameType,
  LOBBY_ICON_LABELS,
  LobbyIcon,
  PokerVariant,
  getGameInfoForVariant,
} from '@repo/common-types';
import { Button, Card, CardBody, Col, Container, Row, Spinner } from 'reactstrap';
import { maxPlayersOptions } from '@/common/forms/options/MaxPlayerOptions';
import checkVariants from '@/common/forms/table-handling/checkVariants';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { AxiosError } from 'axios';
import FormFields from '@/common/forms/FormFields';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

const TemplateCreate = () => {
  const navigate = useNavigate();

  const [isLimitGame, setIsLimitGame] = useState<boolean>(false);
  const [isStudGame, setIsStudGame] = useState<boolean>(false);
  const [submission, setSubmission] = useState<boolean>(false);
  const [selectedLobbyIcons, setSelectedLobbyIcons] = useState<LobbyIcon[]>([]);

  const createRedirection = () => {
    navigate(routes.TEMPLATES);
  };

  const queryClient = useQueryClient();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Template Name is required'),
    game: Yup.string().required('Game Name is required'),
    variants: Yup.string().oneOf(Object.values(PokerVariant), 'Invalid variant').required('Variants are required'),
    minBuyin: Yup.number().required('Min Buyin is required'),
    maxBuyin: Yup.number()
      .required('Max Buyin is required')
      .moreThan(Yup.ref('minBuyin'), 'Max Buyin should be greater than Min Buyin'),
    smallBlind: Yup.number().required('Small Blind is required'),
    bigBlind: Yup.number()
      .required('Big Blind is required')
      .moreThan(Yup.ref('smallBlind'), 'Big Blind should be greater than Small Blind'),
    smallBet: Yup.number().when('isLimitGame', {
      is: true,
      then: (schema) => schema.required('Small Bet is required'),
      otherwise: (schema) => schema.nullable(),
    }),
    bigBet: Yup.number().when('isLimitGame', {
      is: true,
      then: (schema) => schema.required('Big Bet is required'),
      otherwise: (schema) => schema.nullable(),
    }),
    bettingCap: Yup.number().when('isLimitGame', {
      is: true,
      then: (schema) => schema.required('Betting Cap is required'),
      otherwise: (schema) => schema.nullable(),
    }),
    bringIn: Yup.number().when('isStudGame', {
      is: true,
      then: (schema) => schema.required('Bring In is required'),
      otherwise: (schema) => schema.nullable(),
    }),
    ante: Yup.number().required('Ante is required'),
    maxPlayers: Yup.number().required('Max Players is required'),
    isLimitGame: Yup.boolean(),
    isStudGame: Yup.boolean(),
    id: Yup.number().notRequired(),
    lobbyIcons: Yup.array().of(Yup.string()).optional(),
  });

  type ValidationSchema = Yup.InferType<typeof validationSchema>;

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    register,
  } = useForm<ValidationSchema>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      game: '',
      minBuyin: 0,
      maxBuyin: 0,
      smallBlind: 0,
      bigBlind: 0,
      smallBet: 0,
      bigBet: 0,
      bettingCap: 0,
      bringIn: 0,
      ante: 0,
      maxPlayers: 0,
      isLimitGame,
      isStudGame,
      lobbyIcons: [],
    },
  });

  const selectedGame = watch('game') as GameType | '';
  const selectedVariant = watch('variants');

  const gameOptions = useMemo(() => {
    const registryOptions = Object.entries(GameRegistry).map(([type, info]) => ({
      value: type,
      label: info.name,
    }));

    if (selectedGame && !registryOptions.some((option) => option.value === selectedGame)) {
      registryOptions.push({
        value: selectedGame,
        label: selectedGame,
      });
    }

    return registryOptions.sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedGame]);

  const currentGameInfo = useMemo(() => {
    if (!selectedGame || !(selectedGame in GameRegistry)) {
      return undefined;
    }

    return GameRegistry[selectedGame as GameType];
  }, [selectedGame]);

  const variantList = useMemo<PokerVariant[]>(() => {
    if (currentGameInfo?.variants?.length) {
      return currentGameInfo.variants;
    }
    if (selectedVariant) {
      return [selectedVariant as PokerVariant];
    }
    return [];
  }, [currentGameInfo, selectedVariant]);

  useEffect(() => {
    const defaultVariant = variantList[0] ?? '';
    if (defaultVariant && selectedVariant !== defaultVariant) {
      setValue('variants', defaultVariant as ValidationSchema['variants'], {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else if (!defaultVariant && selectedVariant) {
      setValue('variants', '' as ValidationSchema['variants'], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [variantList, selectedVariant, setValue]);

  useEffect(() => {
    if (!variantList.length) {
      setIsStudGame(false);
      setIsLimitGame(false);
      return;
    }
    const { stud, limit } = checkVariants(variantList);
    setIsStudGame(stud);
    setIsLimitGame(limit);
  }, [variantList]);

  const onSubmit = async (formData: ValidationSchema) => {
    const variants: PokerVariant[] = variantList;
    if (!variants.length) {
      showToast('Please, review the data provided.', 'warning');
      return;
    }

    setSubmission(true);
    try {
      const representativeVariant = variants[0] as PokerVariant;
      const variantInfo = getGameInfoForVariant(representativeVariant);
      const response = await createTemplateRest({
        name: formData.name,
        game: formData.game,
        variants,
        maxPlayers: formData.maxPlayers,
        ante: formData.ante,
        maxBuyin: formData.maxBuyin,
        minBuyin: formData.minBuyin,
        smallBlind: formData.smallBlind,
        bigBlind: formData.bigBlind,
        bringIn: isStudGame ? (formData.bringIn ? formData.bringIn : undefined) : undefined,
        bettingCap: isLimitGame ? (formData.bettingCap ? formData.bettingCap : undefined) : undefined,
        smallBet: isLimitGame ? (formData.smallBet ? formData.smallBet : undefined) : undefined,
        bigBet: isLimitGame ? (formData.bigBet ? formData.bigBet : undefined) : undefined,
        // Add missing required fields using game registry
        category: variantInfo.category,
        minStake: isLimitGame ? (formData.smallBet ? formData.smallBet : 0) : formData.smallBlind,
        maxStake: isLimitGame ? (formData.bigBet ? formData.bigBet : 0) : formData.bigBlind,
        bettingStructure: variantInfo.bettingStructure,
        isPublic: true, // Default to public
        lobbyIcons: selectedLobbyIcons,
      });

      if ((response.status === 201 || response.status === 200) && response.data.id) {
        await queryClient.invalidateQueries({ queryKey: ['fetchTables'] });
        showToast('Template created successfully', 'success');
        createRedirection();
        setSubmission(true);
        return;
      }
    } catch (error) {
      setSubmission(false);
      if (error instanceof AxiosError && error?.response?.data) {
        const customError = error.response.data as {
          message: string[];
          statusCode: number;
          error: string;
        };
        if (customError.statusCode === 400 && customError.message) {
          customError.message.forEach((element) => {
            showToast(element, 'warning');
          });
          return;
        } else {
          showToast(error.message, 'warning');
        }
      }
    }
  };

  const handleLobbyIconToggle = (icon: LobbyIcon) => {
    setSelectedLobbyIcons((prev) => (prev.includes(icon) ? prev.filter((i) => i !== icon) : [...prev, icon]));
  };

  return (
    <React.Fragment>
      <div className="page-content mb-6">
        <Container fluid>
          <Row className="justify-content-center">
            <Col lg={12}>
              <Card className="card mb-2">
                <CardBody className="card-body">
                  <h3 className="mb-5">Cash Game Template Creation</h3>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <FormFields>
                      <FormField name="name" control={control} label="Template Name" error={errors.name?.message} />
                      <FormField
                        type="select"
                        name="game"
                        control={control}
                        label="Game Name"
                        error={errors.game?.message}
                        options={gameOptions}
                      />
                    </FormFields>
                    <input type="hidden" {...register('variants')} />
                    <FormFields>
                      <Col lg={5} sm={12} className="mb-2">
                        <label>Variants</label>
                        <div className="form-control-plaintext">
                          {variantList.length ? variantList.join(', ') : '—'}
                        </div>
                        {errors.variants && <div className="text-danger small mt-1">{errors.variants.message}</div>}
                      </Col>
                    </FormFields>
                    <FormFields>
                      <FormField
                        name="minBuyin"
                        control={control}
                        label="Min Buyin"
                        error={errors.minBuyin?.message}
                        prefix="$"
                      />
                      <FormField
                        name="maxBuyin"
                        control={control}
                        label="Max Buyin"
                        error={errors.maxBuyin?.message}
                        prefix="$"
                      />
                    </FormFields>
                    <FormFields>
                      <FormField
                        name="smallBlind"
                        control={control}
                        label="Small Blind"
                        error={errors.smallBlind?.message}
                        prefix="$"
                      />
                      <FormField
                        name="bigBlind"
                        control={control}
                        label="Big Blind"
                        error={errors.bigBlind?.message}
                        prefix="$"
                      />
                    </FormFields>
                    <FormFields>
                      <FormField
                        name="smallBet"
                        control={control}
                        label="Small Bet"
                        error={errors.smallBet?.message}
                        prefix="$"
                      />
                      <FormField
                        name="bigBet"
                        control={control}
                        label="Big Bet"
                        error={errors.bigBet?.message}
                        prefix="$"
                      />
                    </FormFields>
                    <FormField
                      name="bettingCap"
                      control={control}
                      label="Betting Cap"
                      error={errors.bettingCap?.message}
                      prefix="$"
                    />
                    <FormField
                      name="bringIn"
                      control={control}
                      label="Bring In"
                      error={errors.bringIn?.message}
                      prefix="$"
                    />
                    <FormFields>
                      <FormField name="ante" control={control} label="Ante" error={errors.ante?.message} prefix="$" />
                      <FormField
                        type="select"
                        name="maxPlayers"
                        control={control}
                        label="Table Size"
                        error={errors.maxPlayers?.message}
                        options={maxPlayersOptions()}
                      />
                    </FormFields>

                    {/* Table Features Section */}
                    <div className="mb-4">
                      <h5 className="mb-3">Table Features</h5>
                      <div className="row">
                        {Object.values(LobbyIcon).map((icon) => (
                          <div key={icon} className="col-md-4 col-sm-6 mb-2">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`lobbyIcon-${icon}`}
                                checked={selectedLobbyIcons.includes(icon)}
                                onChange={() => handleLobbyIconToggle(icon)}
                              />
                              <label className="form-check-label" htmlFor={`lobbyIcon-${icon}`}>
                                {LOBBY_ICON_LABELS[icon]}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Col lg={10} className="mt-5">
                      <div className="d-flex justify-content-end mt-4 flex-row">
                        <Button type="submit" className="my-4 ms-5" color="primary" disabled={submission}>
                          {submission && <Spinner size={'sm'} className="mx-2" />} Create
                        </Button>
                      </div>
                    </Col>
                  </form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TemplateCreate;
